import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import AdminNavbar from "../Layout/AdminNavbar";
import API_BASE_URL from "../config";

const pages = [
    { id: "announcement", label: "Announcement", icon: "📢", desc: "Post and manage company-wide announcements", color: "#d97706", bg: "#fffbeb", border: "#fde68a" },
    { id: "employee_evaluation", label: "Employee Evaluation", icon: "📊", desc: "Access and submit performance evaluations", color: "#7c3aed", bg: "#f5f3ff", border: "#ddd6fe" },
    { id: "asset_management", label: "Asset Management", icon: "🧰", desc: "Full control over asset register and operations", color: "#0369a1", bg: "#f0f9ff", border: "#bae6fd" },
    { id: "analytics", label: "Timesheet Analytics", icon: "📅", desc: "View and analyze employee timesheet data", color: "#0891b2", bg: "#ecfeff", border: "#a5f3fc" },
];

const NAVBAR_HEIGHT = 64;

const EMP_COLORS = [
    ["#1e3a5f", "#e8f0fe"],
    ["#3b1f5e", "#ede9fe"],
    ["#1a3c34", "#d1fae5"],
    ["#4a1942", "#fce7f3"],
    ["#1c2f45", "#dbeafe"],
    ["#3b2a14", "#fef3c7"],
];

const empColorPair = (id) => EMP_COLORS[id % EMP_COLORS.length];

const getInitials = (firstName = "", lastName = "") =>
    `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();

function EmpAvatar({ emp, size = 36, fontSize = 13 }) {
    const [imgError, setImgError] = useState(false);
    const showPhoto = emp.photoPath && !imgError;
    const [fg, bg] = empColorPair(emp.id);
    const base = { width: size, height: size, borderRadius: "50%", flexShrink: 0, overflow: "hidden" };

    if (showPhoto) return (
        <div style={base}>
            <img
                src={`${API_BASE_URL}/uploads/${emp.photoPath}`}
                alt={`${emp.firstName} ${emp.lastName}`}
                onError={() => setImgError(true)}
                style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center top" }}
            />
        </div>
    );
    return (
        <div style={{ ...base, display: "flex", alignItems: "center", justifyContent: "center", background: bg, color: fg, fontSize, fontWeight: 700, letterSpacing: "-0.3px" }}>
            {getInitials(emp.firstName, emp.lastName)}
        </div>
    );
}

function Toggle({ checked, color }) {
    return (
        <div style={{
            width: 42,
            height: 24,
            borderRadius: 99,
            background: checked ? color : "#e2e8f0",
            position: "relative",
            transition: "background 0.25s ease",
            flexShrink: 0,
            cursor: "pointer",
        }}>
            <div style={{
                position: "absolute",
                top: 3,
                left: checked ? 21 : 3,
                width: 18,
                height: 18,
                borderRadius: "50%",
                background: "#fff",
                boxShadow: "0 1px 4px rgba(0,0,0,0.18)",
                transition: "left 0.25s cubic-bezier(.4,0,.2,1)",
            }} />
        </div>
    );
}

export default function AdminPermissions() {
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [permissions, setPermissions] = useState({});
    const [selectedEmp, setSelectedEmp] = useState(null);
    const [saved, setSaved] = useState(false);
    const [saving, setSaving] = useState(false);
    const [search, setSearch] = useState("");

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const token = localStorage.getItem("token");
                const [empRes, permRes] = await Promise.all([
                    axios.get(`${API_BASE_URL}/api/employees/active/summary`, { headers: { Authorization: `Bearer ${token}` } }),
                    axios.get(`${API_BASE_URL}/api/permissions`, { headers: { Authorization: `Bearer ${token}` } }),
                ]);
                const data = empRes.data;
                setEmployees(data);
                const init = {};
                data.forEach(emp => {
                    init[emp.id] = {};
                    pages.forEach(page => { init[emp.id][page.id] = permRes.data?.[emp.id]?.[page.id] ?? false; });
                });
                setPermissions(init);
                if (data.length > 0) setSelectedEmp(data[0]);
            } catch {
                toast.error("Failed to load employees.");
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    const toggle = (empId, pageId) => {
        setPermissions(prev => ({ ...prev, [empId]: { ...prev[empId], [pageId]: !prev[empId][pageId] } }));
        setSaved(false);
    };

    const toggleAll = (empId, value) => {
        const updated = {};
        pages.forEach(p => { updated[p.id] = value; });
        setPermissions(prev => ({ ...prev, [empId]: updated }));
        setSaved(false);
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            const token = localStorage.getItem("token");
            await axios.put(
                `${API_BASE_URL}/api/permissions/${selectedEmp.id}`,
                { pages: permissions[selectedEmp.id] },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setSaved(true);
            toast.success("Permissions saved successfully.");
            setTimeout(() => setSaved(false), 2500);
        } catch {
            toast.error("Failed to save permissions.");
        } finally {
            setSaving(false);
        }
    };

    const activeCount = (empId) => Object.values(permissions[empId] || {}).filter(Boolean).length;

    const filtered = employees.filter(e => {
        const q = search.toLowerCase();
        return `${e.firstName} ${e.lastName}`.toLowerCase().includes(q)
            || (e.department || "").toLowerCase().includes(q)
            || (e.designation || "").toLowerCase().includes(q);
    });

    return (
        <div style={{ height: "100vh", overflow: "hidden", display: "flex", flexDirection: "column", fontFamily: "'DM Sans', 'Geist', system-ui, sans-serif", background: "#f4f6f9" }}>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=DM+Mono:wght@500&display=swap');

                @keyframes spin { to { transform: rotate(360deg); } }
                @keyframes slideIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }

                .emp-row { transition: background 0.12s; cursor: pointer; }
                .emp-row:hover { background: #f1f5f9 !important; }

                .perm-card { transition: box-shadow 0.18s, border-color 0.18s, transform 0.15s; }
                .perm-card:hover { transform: translateY(-1px); }
                .perm-row:hover td { background: #f8f7ff; }

                .save-btn { transition: all 0.18s; }
                .save-btn:hover:not(:disabled) { filter: brightness(1.06); transform: translateY(-1px); }

                .action-btn { transition: all 0.15s; }
                .action-btn:hover:not(:disabled) { background: #e2e8f0 !important; color: #1e293b !important; }

                .sidebar-scroll::-webkit-scrollbar { width: 4px; }
                .sidebar-scroll::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 99px; }

                .main-scroll::-webkit-scrollbar { width: 5px; }
                .main-scroll::-webkit-scrollbar-thumb { background: #dde1e7; border-radius: 99px; }
                .main-scroll::-webkit-scrollbar-track { background: transparent; }

                .perm-thead th {
                    position: sticky;
                    top: 0;
                    z-index: 2;
                    background: #f8fafc;
                    box-shadow: 0 1px 0 #e8ecf0;
                }

                .search-box:focus { border-color: #6366f1 !important; box-shadow: 0 0 0 3px rgba(99,102,241,0.1); }
            `}</style>

            {/* Navbar */}
            <div style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 1000, height: NAVBAR_HEIGHT }}>
                <AdminNavbar />
            </div>

            <div style={{ marginTop: NAVBAR_HEIGHT+35, height: `calc(100vh - ${NAVBAR_HEIGHT}px)`, display: "flex", overflow: "hidden" }}>

                {/* ─── SIDEBAR ─── */}
                <div style={{
                    width: 280,
                    flexShrink: 0,
                    background: "#fff",
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    borderRight: "1px solid #e8ecf0",
                }}>
                    {/* Header */}
                    <div style={{ padding: "20px 20px 16px", borderBottom: "1px solid #f1f5f9", flexShrink: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
                            <div style={{
                                width: 36, height: 36, borderRadius: 10,
                                background: "#f8fafc",
                                border: "1px solid #e2e8f0",
                                display: "flex", alignItems: "center", justifyContent: "center",
                                fontSize: 17, flexShrink: 0
                            }}>
                                🛡️
                            </div>
                            <div>
                                <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a", letterSpacing: "-0.2px" }}>Access Control</div>
                                <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 1 }}>
                                    {loading ? "Loading…" : `${employees.length} team members`}
                                </div>
                            </div>
                        </div>

                        {/* Search */}
                        <div style={{ position: "relative" }}>
                            <svg style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)" }} width="13" height="13" fill="none" viewBox="0 0 24 24" stroke="#94a3b8" strokeWidth="2.5">
                                <circle cx="11" cy="11" r="8" /><path strokeLinecap="round" d="M21 21l-4.35-4.35" />
                            </svg>
                            <input
                                className="search-box"
                                style={{
                                    width: "100%",
                                    border: "1px solid #e2e8f0",
                                    outline: "none",
                                    background: "#f8fafc",
                                    color: "#334155",
                                    fontSize: 13,
                                    borderRadius: 8,
                                    padding: "8px 10px 8px 32px",
                                    boxSizing: "border-box",
                                    transition: "border-color 0.15s, box-shadow 0.15s"
                                }}
                                placeholder="Search by name, role…"
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                            />
                            {search && (
                                <span onClick={() => setSearch("")}
                                    style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", color: "#94a3b8", cursor: "pointer", fontSize: 18, lineHeight: 1 }}>×</span>
                            )}
                        </div>
                    </div>

                    {/* Employee List */}
                    <div className="sidebar-scroll" style={{ overflowY: "auto", flex: 1, padding: "8px 0" }}>
                        {loading ? (
                            <div style={{ display: "flex", justifyContent: "center", padding: 32 }}>
                                <div style={{ width: 22, height: 22, border: "2.5px solid #e2e8f0", borderTop: "2.5px solid #6366f1", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
                            </div>
                        ) : filtered.length === 0 ? (
                            <div style={{ padding: "32px 16px", textAlign: "center", color: "#94a3b8", fontSize: 12 }}>
                                <div style={{ fontSize: 28, marginBottom: 8 }}>🔍</div>
                                No employees found
                            </div>
                        ) : filtered.map(emp => {
                            const isActive = selectedEmp?.id === emp.id;
                            const count = activeCount(emp.id);
                            return (
                                <div
                                    key={emp.id}
                                    className="emp-row"
                                    onClick={() => setSelectedEmp(emp)}
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 11,
                                        padding: "10px 18px",
                                        background: isActive ? "#f8f7ff" : "transparent",
                                        borderLeft: `3px solid ${isActive ? "#6366f1" : "transparent"}`,
                                        position: "relative"
                                    }}
                                >
                                    <EmpAvatar emp={emp} size={36} fontSize={12} />
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{
                                            fontSize: 13, fontWeight: isActive ? 600 : 500,
                                            color: isActive ? "#1e1b4b" : "#334155",
                                            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"
                                        }}>
                                            {emp.firstName} {emp.lastName}
                                        </div>
                                        {/* <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                            {emp.designation || emp.department || "—"}
                                        </div> */}
                                    </div>

                                    {/* Permission count badge */}
                                    <div style={{
                                        fontSize: 10,
                                        fontWeight: 700,
                                        fontFamily: "'DM Mono', monospace",
                                        color: count === pages.length ? "#059669" : count === 0 ? "#94a3b8" : "#d97706",
                                        background: count === pages.length ? "#d1fae5" : count === 0 ? "#f1f5f9" : "#fef3c7",
                                        padding: "2px 7px",
                                        borderRadius: 99,
                                        flexShrink: 0
                                    }}>
                                        {count}/{pages.length}
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Sidebar Footer */}
                    <div style={{ padding: "12px 18px", borderTop: "1px solid #f1f5f9", flexShrink: 0 }}>
                        <div style={{ fontSize: 11, color: "#cbd5e1", display: "flex", alignItems: "center", gap: 6 }}>
                            <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#10b981" }} />
                            {employees.filter(e => activeCount(e.id) > 0).length} employees have access
                        </div>
                    </div>
                </div>

                {/* ─── MAIN PANEL ─── */}
                <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", overflow: "hidden", background: "#f4f6f9" }}>
                    {!selectedEmp ? (
                        <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 10 }}>
                            <div style={{ fontSize: 48, opacity: 0.2 }}>🛡️</div>
                            <div style={{ fontSize: 15, fontWeight: 600, color: "#64748b" }}>Select a team member</div>
                            <div style={{ fontSize: 13, color: "#94a3b8" }}>Choose from the list to manage their access</div>
                        </div>
                    ) : (
                        <>
                            {/* ── Employee Header ── */}
                            <div style={{
                                background: "#fff",
                                borderBottom: "1px solid #e8ecf0",
                                padding: "18px 28px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 16,
                                flexWrap: "wrap",
                                flexShrink: 0,
                                animation: "slideIn 0.2s ease"
                            }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                                    <div style={{ position: "relative" }}>
                                        <div style={{ padding: 2, borderRadius: "50%", border: "2px solid #e2e8f0" }}>
                                            <EmpAvatar emp={selectedEmp} size={46} fontSize={17} />
                                        </div>
                                        <div style={{ position: "absolute", bottom: 2, right: 2, width: 10, height: 10, borderRadius: "50%", background: "#10b981", border: "2px solid #fff" }} />
                                    </div>
                                    <div>
                                        <div style={{ fontSize: 17, fontWeight: 700, color: "#0f172a", letterSpacing: "-0.3px" }}>
                                            {selectedEmp.firstName} {selectedEmp.lastName}
                                        </div>
                                        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 5 }}>
                                            {[selectedEmp.employeeId, selectedEmp.designation, selectedEmp.department].filter(Boolean).map((chip, i) => (
                                                <span key={i} style={{
                                                    fontSize: 11, fontWeight: 500,
                                                    padding: "2px 9px", borderRadius: 99,
                                                    background: "#f1f5f9", color: "#64748b",
                                                    border: "1px solid #e2e8f0"
                                                }}>{chip}</span>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                {/* Action buttons */}
                                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                    <button
                                        className="action-btn"
                                        disabled={saving}
                                        onClick={() => toggleAll(selectedEmp.id, true)}
                                        style={{
                                            padding: "8px 16px",
                                            background: "#f8fafc",
                                            color: "#334155",
                                            border: "1px solid #e2e8f0",
                                            borderRadius: 8,
                                            fontSize: 12,
                                            fontWeight: 600,
                                            cursor: "pointer",
                                            fontFamily: "inherit"
                                        }}
                                    >
                                        Grant All
                                    </button>
                                    <button
                                        className="action-btn"
                                        disabled={saving}
                                        onClick={() => toggleAll(selectedEmp.id, false)}
                                        style={{
                                            padding: "8px 16px",
                                            background: "#f8fafc",
                                            color: "#334155",
                                            border: "1px solid #e2e8f0",
                                            borderRadius: 8,
                                            fontSize: 12,
                                            fontWeight: 600,
                                            cursor: "pointer",
                                            fontFamily: "inherit"
                                        }}
                                    >
                                        Revoke All
                                    </button>
                                </div>
                            </div>

                            {/* ── Progress Strip ── */}
                            <div style={{
                                background: "#fff",
                                borderBottom: "1px solid #e8ecf0",
                                padding: "10px 28px",
                                display: "flex",
                                alignItems: "center",
                                gap: 14,
                                flexShrink: 0
                            }}>
                                <span style={{ fontSize: 11, fontWeight: 600, color: "#94a3b8", letterSpacing: "0.5px", textTransform: "uppercase", whiteSpace: "nowrap" }}>
                                    Access Level
                                </span>
                                <div style={{ flex: 1, height: 4, background: "#f1f5f9", borderRadius: 99, overflow: "hidden" }}>
                                    <div style={{
                                        height: "100%",
                                        width: `${(activeCount(selectedEmp.id) / pages.length) * 100}%`,
                                        background: activeCount(selectedEmp.id) === pages.length ? "#10b981" : activeCount(selectedEmp.id) > 0 ? "#6366f1" : "#e2e8f0",
                                        borderRadius: 99,
                                        transition: "width 0.4s ease, background 0.3s"
                                    }} />
                                </div>
                                <span style={{ fontSize: 12, fontWeight: 700, color: "#334155", fontFamily: "'DM Mono', monospace", minWidth: 30 }}>
                                    {activeCount(selectedEmp.id)}/{pages.length}
                                </span>
                            </div>

                            {/* ── Permission Table ── */}
                            <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", padding: "20px 28px 0" }}>

                                {/* Table header row (outside scroll) */}
                                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.8px", textTransform: "uppercase", color: "#94a3b8", marginBottom: 10 }}>
                                    Page Permissions
                                </div>

                                {/* Scrollable table wrapper */}
                                <div className="main-scroll" style={{ flex: 1, overflowY: "auto", borderRadius: 12, border: "1px solid #e8ecf0", background: "#fff" }}>
                                    <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
                                        <colgroup>
                                            <col style={{ width: 44 }} />  {/* icon */}
                                            <col />                         {/* page name */}
                                            <col style={{ width: "38%" }} />{/* description */}
                                            <col style={{ width: 110 }} />  {/* status */}
                                            <col style={{ width: 90 }} />   {/* access toggle */}
                                        </colgroup>
                                        <thead className="perm-thead">
                                            <tr>
                                                <th style={{ padding: "11px 14px", textAlign: "left" }}></th>
                                                <th style={{ padding: "11px 14px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "#64748b", letterSpacing: "0.5px", textTransform: "uppercase" }}>Page</th>
                                                <th style={{ padding: "11px 14px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "#64748b", letterSpacing: "0.5px", textTransform: "uppercase" }}>Description</th>
                                                <th style={{ padding: "11px 14px", textAlign: "center", fontSize: 11, fontWeight: 700, color: "#64748b", letterSpacing: "0.5px", textTransform: "uppercase" }}>Status</th>
                                                <th style={{ padding: "11px 14px", textAlign: "center", fontSize: 11, fontWeight: 700, color: "#64748b", letterSpacing: "0.5px", textTransform: "uppercase" }}>Access</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {pages.map((page, idx) => {
                                                const allowed = permissions[selectedEmp.id]?.[page.id];
                                                return (
                                                    <tr
                                                        key={page.id}
                                                        onClick={() => toggle(selectedEmp.id, page.id)}
                                                        className="perm-row"
                                                        style={{
                                                            borderBottom: idx < pages.length - 1 ? "1px solid #f1f5f9" : "none",
                                                            background: allowed ? page.bg + "55" : "#fff",
                                                            cursor: "pointer",
                                                            transition: "background 0.18s",
                                                            animation: `slideIn 0.18s ease ${idx * 0.06}s both`,
                                                            userSelect: "none",
                                                        }}
                                                    >
                                                        {/* Icon cell */}
                                                        <td style={{ padding: "20px 8px 20px 16px", verticalAlign: "middle" }}>
                                                            <div style={{
                                                                width: 36, height: 36, borderRadius: 9,
                                                                background: allowed ? page.bg : "#f1f5f9",
                                                                border: `1px solid ${allowed ? page.border : "#e8ecf0"}`,
                                                                display: "flex", alignItems: "center", justifyContent: "center",
                                                                fontSize: 17, flexShrink: 0,
                                                                transition: "all 0.2s"
                                                            }}>
                                                                {page.icon}
                                                            </div>
                                                        </td>

                                                        {/* Page name */}
                                                        <td style={{ padding: "20px 14px", verticalAlign: "middle" }}>
                                                            <div style={{ fontSize: 13, fontWeight: 600, color: allowed ? "#0f172a" : "#475569", transition: "color 0.2s", whiteSpace: "nowrap" }}>
                                                                {page.label}
                                                            </div>
                                                        </td>

                                                        {/* Description */}
                                                        <td style={{ padding: "20px 14px", verticalAlign: "middle" }}>
                                                            <div style={{ fontSize: 12, color: "#94a3b8", lineHeight: 1.5 }}>{page.desc}</div>
                                                        </td>

                                                        {/* Status badge */}
                                                        <td style={{ padding: "14px 14px", verticalAlign: "middle", textAlign: "center" }}>
                                                            <span style={{
                                                                display: "inline-flex", alignItems: "center", gap: 5,
                                                                fontSize: 11, fontWeight: 600,
                                                                padding: "4px 12px", borderRadius: 99,
                                                                background: allowed ? page.bg : "#f1f5f9",
                                                                color: allowed ? page.color : "#94a3b8",
                                                                border: `1px solid ${allowed ? page.border : "#e8ecf0"}`,
                                                                transition: "all 0.2s",
                                                                whiteSpace: "nowrap"
                                                            }}>
                                                                <span style={{
                                                                    width: 6, height: 6, borderRadius: "50%",
                                                                    background: allowed ? page.color : "#cbd5e1",
                                                                    flexShrink: 0
                                                                }} />
                                                                {allowed ? "Allowed" : "Denied"}
                                                            </span>
                                                        </td>

                                                        {/* Toggle */}
                                                        <td style={{ padding: "14px 16px", verticalAlign: "middle", textAlign: "center" }}>
                                                            <Toggle checked={allowed} color={page.color} />
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Info note */}
                                <div style={{ margin: "14px 0 14px", padding: "11px 16px", background: "#fafafa", border: "1px solid #f1f5f9", borderRadius: 10, display: "flex", alignItems: "flex-start", gap: 10, flexShrink: 0 }}>
                                    <span style={{ fontSize: 13, flexShrink: 0, marginTop: 1 }}>ℹ️</span>
                                    <p style={{ margin: 0, fontSize: 12, color: "#64748b", lineHeight: 1.6 }}>
                                        Changes apply immediately after saving. Revoked access takes effect on the employee's next session.
                                    </p>
                                </div>
                            </div>

                            {/* ── Sticky Footer ── */}
                            <div style={{
                                flexShrink: 0,
                                background: "rgba(255,255,255,0.96)",
                                backdropFilter: "blur(10px)",
                                borderTop: "1px solid #e8ecf0",
                                padding: "14px 28px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 12,
                            }}>
                                <div style={{ fontSize: 12, color: saved ? "#059669" : "#94a3b8", fontWeight: saved ? 600 : 400, display: "flex", alignItems: "center", gap: 6 }}>
                                    {saved ? (
                                        <><span style={{ width: 18, height: 18, borderRadius: "50%", background: "#d1fae5", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 10 }}>✓</span> Saved successfully</>
                                    ) : "Unsaved changes will be lost on navigation"}
                                </div>
                                <button
                                    className="save-btn"
                                    disabled={saving}
                                    onClick={handleSave}
                                    style={{
                                        background: saved ? "#059669" : "#1e1b4b",
                                        color: "#fff",
                                        border: "none",
                                        borderRadius: 9,
                                        padding: "10px 28px",
                                        fontSize: 13,
                                        fontWeight: 600,
                                        cursor: saving ? "not-allowed" : "pointer",
                                        opacity: saving ? 0.6 : 1,
                                        boxShadow: saved
                                            ? "0 4px 14px rgba(5,150,105,0.3)"
                                            : "0 4px 14px rgba(30,27,75,0.25)",
                                        letterSpacing: "-0.1px",
                                        minWidth: 100,
                                        fontFamily: "inherit",
                                        transition: "all 0.18s"
                                    }}
                                >
                                    {saving ? "Saving…" : saved ? "✓  Saved" : "Save "}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}